import { findIndex } from 'lodash';
import { wrapGrid } from 'animate-css-grid';
import { take, takeUntil, takeWhile } from 'rxjs/operators';
import { Subject, Subscription, combineLatest, interval } from 'rxjs';
import { Input, OnInit, Output, Component, EventEmitter, ChangeDetectorRef, ElementRef, Renderer2 } from '@angular/core';

import { QuestActionDefinitionDefinitionGamePuzzleThresholdType, QuestActivityType } from '@sifca-monorepo/widget-generator';

import {
  ImageService,
  MobileQuestsService,
  MobileReputationService,
  MobileThemeService,
  TimeBlockLimitationService,
  ToasterService,
} from '@sifca-monorepo/clients';
import { TranslateService } from '@ngx-translate/core';

@Component({
  template: '',
})
export class MobilePuzzleComponent implements OnInit {
  protected subscription: Subscription;
  protected unsubscribeAll: Subject<any> = new Subject<any>();

  @Input() timer: number;
  @Input() timeLeft: number;
  @Input() hasAmount: boolean;

  @Output() argsUpdate = new EventEmitter<any>();
  @Output() performActivity = new EventEmitter<any>();
  @Output() puzzleDisplayChange = new EventEmitter<boolean>();

  grid: any;
  tiles: any;
  prize: any;
  moveCount = 0;
  imgData: string;
  puzzleKeys: any;
  showImage = false;
  darkMode: boolean;
  obs = interval(1000);
  displayedTime: string;
  retryTime: string = '';
  isLastActivity: boolean;
  timerOn: boolean = false;
  timeBlockLimitation: number;
  isRetryButtonDisabled = true;
  isMobile = window.innerWidth <= 900;
  questActivities: QuestActivityType[];
  selectedQuestActivity: QuestActivityType;
  activity$ = this.questsService.selectedQuestActivity$;
  thresholds: QuestActionDefinitionDefinitionGamePuzzleThresholdType[];

  constructor(
    protected element: ElementRef,
    protected cd: ChangeDetectorRef,
    protected renderer: Renderer2,
    protected translate: TranslateService,
    protected imageService: ImageService,
    protected toasterService: ToasterService,
    protected themeService: MobileThemeService,
    protected questsService: MobileQuestsService,
    protected timeService: TimeBlockLimitationService,
    protected reputationService: MobileReputationService,
  ) {
    this.puzzleKeys = {
      A: ['B', 'D'],
      B: ['A', 'C', 'E'],
      C: ['B', 'F'],
      D: ['A', 'E', 'G'],
      E: ['B', 'D', 'F', 'H'],
      F: ['C', 'E', 'I'],
      G: ['D', 'H'],
      H: ['E', 'G', 'I'],
      I: ['F', 'H'],
    };
  }

  ngOnInit(): void {
    this.timeLeft = this.timeService.getTimeLeft();
    this.displayedTime = this.displayTime(+this.timeLeft);
    this.timeBlockLimitation = this.timeService.getLimitation();
    this.questsService.questActivitesByQuest$.pipe(takeUntil(this.unsubscribeAll)).subscribe((questActivitesByQuest) => {
      this.questActivities = questActivitesByQuest;
    });
    this.questsService.selectedQuestActivity$.pipe(takeUntil(this.unsubscribeAll)).subscribe((questActivity) => {
      this.selectedQuestActivity = questActivity;
      this.thresholds = this.selectedQuestActivity?.activity?.action?.definition?.game?.sliding?.threshold;
      if (questActivity) {
        this.isLastActivity = this.selectedQuestActivity?.id === this.questActivities[this.questActivities?.length - 1]?.id;
      }
      this.grid = this.element.nativeElement.querySelector('.grid') as HTMLElement;
      let canvas = this.element.nativeElement.appendChild(document.createElement('canvas') as HTMLCanvasElement);
      canvas.width = 480;
      canvas.height = 480;
      canvas.style.display = 'none';
      let context = canvas.getContext('2d')!;
      let img = new Image();
      const encodedPath = encodeURIComponent(questActivity?.activity?.action?.definition?.game?.sliding?.picture?.path);
      this.imgData = `${questActivity?.activity?.action?.definition?.game?.sliding?.picture.baseUrl}/${encodedPath}`;
      img.src = this.imgData;
      img.onload = () => {
        context.drawImage(img, 0, 0, 480, 480);
        if (this.element.nativeElement.querySelector('#puzzle-img')) {
          (this.element.nativeElement.querySelector('#puzzle-img') as HTMLImageElement).src = this.imgData;
        }
        this.tiles = this.element.nativeElement.querySelectorAll('.tile');
        for (let i = 0; i < this.tiles.length; i++) {
          let tile = this.tiles[i] as HTMLElement;
          tile.style.backgroundImage = 'url(' + this.imgData + ')';
          if (tile.classList.contains('tile--empty')) {
            tile.style.opacity = '0.5';
          }
        }
        this.shuffle();
        this.addListeners();
      };
      this.cd.markForCheck();
    });
    this.themeService.darkMode$.pipe(takeUntil(this.unsubscribeAll)).subscribe((darkMode) => {
      this.darkMode = darkMode;
      this.cd.detectChanges();
    });
  }

  showImageToggle() {
    this.showImage = !this.showImage;
    this.cd.markForCheck();
  }

  exitGame(): void {
    this.puzzleDisplayChange.emit(false);
  }

  displayTime(time: number) {
    if (typeof time !== 'number' || isNaN(time) || time < 0) {
      return '00:00';
    }
    let min = Math.floor(time / 60);
    let sec = time % 60;
    return sec < 10 ? min.toString() + ':0' + sec.toString() : min.toString() + ':' + sec.toString();
  }

  addListeners() {
    Array.from(this.tiles).map((tile) => {
      (tile as HTMLInputElement).addEventListener('click', this.tileMovement);
    });
  }

  tileMovement = (event: MouseEvent) => {
    const { forceGridAnimation } = wrapGrid(this.grid);
    if (this.timerOn == false) {
      if (this.timer) {
        this.timerOn = this.timer ? true : false;
        this.subscription = this.obs.pipe(takeWhile(() => this.timerOn)).subscribe(() => {
          this.timeLeft--;
          this.displayedTime = this.displayTime(this.timeLeft);
          if (this.timeLeft === 0) {
            combineLatest([this.translate.get('FAILURE_MSG'), this.translate.get('REPLAY_GAME')])
              .pipe(take(1))
              .subscribe(([failed, tryAgain]) => {
                this.toasterService.error(failed, tryAgain, null, 8000);
              });
            this.timerOn = false;
            Array.from(this.tiles).map((tile) => ((tile as HTMLInputElement).disabled = true));
            let count = this.timeBlockLimitation * 60;
            const decTimeBlock = () => {
              setTimeout(() => {
                if (count !== 0) {
                  count--;
                  let min = Math.floor(count / 60);
                  let sec = count % 60;
                  let retryTime = sec < 10 ? `${min}:0${sec}` : `${min}:${sec}`;
                  this.retryTime = retryTime;
                  if (retryTime === '0:00') {
                    this.timeLeft = this.timeService.getTimeLeft();
                  }
                  decTimeBlock();
                }
              }, 1);
            };
            decTimeBlock();
          }
        });
      }
    }
    const target = event.target as HTMLElement;
    const currentButton = target.closest('.tile') as HTMLElement | null;

    if (!currentButton) {
      return;
    }
    const tileArea = currentButton.style.getPropertyValue('--area');
    const emptyTile = this.element.nativeElement.querySelector('.tile--empty') as HTMLElement;
    const emptyTileArea = emptyTile.style.getPropertyValue('--area');
    emptyTile.style.setProperty('--area', tileArea);
    currentButton.style.setProperty('--area', emptyTileArea);
    this.moveCount++;
    forceGridAnimation();
    this.unlockTiles(tileArea);
    this.checkIsSolved();
    this.cd.markForCheck();
  };

  unlockTiles(currentTileArea: string) {
    Array.from(this.tiles).map((t) => {
      let tile = t as HTMLInputElement;
      const tileArea = tile.style.getPropertyValue('--area');
      tile.disabled = this.puzzleKeys[currentTileArea].includes(tileArea) ? false : true;
    });
  }

  solvable(puzzleTiles: string[]) {
    let invCount = 0;
    let order = Object.keys(this.puzzleKeys).map((x) => puzzleTiles.indexOf(x) + 1);

    for (let i = 0; i < order.length; i++) {
      if (order[i] == 9) continue;
      for (let j = i + 1; j < order.length; j++) {
        if (order[i] > order[j]) invCount++;
      }
    }
    return invCount % 2 === 0;
  }

  shuffle() {
    let puzzleTiles = Object.keys(this.puzzleKeys);
    puzzleTiles.sort(() => 0.5 - Math.random());
    while (!this.solvable(puzzleTiles)) {
      puzzleTiles.sort(() => 0.5 - Math.random());
    }
    Array.from(this.tiles).map((tile, i) => (tile as HTMLInputElement).style.setProperty('--area', puzzleTiles[i]));
    let emptyTile = this.element.nativeElement.querySelector('.tile--empty') as HTMLInputElement;
    this.unlockTiles(emptyTile?.style.getPropertyValue('--area'));
  }

  checkIsSolved() {
    let currentTiles = Array.from(this.tiles).map((tile) => (tile as HTMLInputElement).style.getPropertyValue('--area'));
    if (JSON.stringify(currentTiles) === JSON.stringify(Object.keys(this.puzzleKeys))) {
      this.timerOn = false;
      this.subscription?.unsubscribe();
      this.navigateNextSlide();
    }
  }

  retry(): void {
    this.moveCount = 0;
    this.isRetryButtonDisabled = true;
    this.timeService.setTimeLeft(this.timeLeft);
    this.displayedTime = this.displayTime(this.timeLeft);
    this.timerOn = false;
    this.shuffle();
    this.retryTime = '';
    this.addListeners();
  }

  navigateNextSlide() {
    const index = findIndex(this.questActivities, (qst) => qst?.id === this.selectedQuestActivity?.id);
    const game = {
      index,
      game: {
        activity: this.selectedQuestActivity?.id,
        movesCount: this.moveCount,
        duration: this.timer - this.timeLeft,
        timer: this.thresholds[0]?.timer,
      },
    };
    if (this.isLastActivity) {
      this.performActivity.emit(game);
    } else {
      let index = findIndex(this.questActivities, (item) => item?.id === this.selectedQuestActivity?.id);
      this.questsService.selectedQuestActivity$ = this.questActivities[index + 1];
      this.argsUpdate.emit(game);
    }
  }

  ngOnDestroy(): void {
    this.timeLeft = -1;
    this.subscription?.unsubscribe();
    this.timerOn = false;
    this.unsubscribeAll.next(true);
    this.unsubscribeAll.complete();
  }
}
